import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames/bind';
import { isEscapeKey } from '@/utils/keyboard';
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  linkPlugin,
  linkDialogPlugin,
  imagePlugin,
  tablePlugin,
  codeBlockPlugin,
  codeMirrorPlugin,
  diffSourcePlugin,
  toolbarPlugin,
  directivesPlugin,
  type MDXEditorMethods,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import styles from './MDXEditorField.module.scss';
import { codeBlockLanguages } from './codeBlockLanguages';
import { secretFieldPlugin } from './plugins/secretFieldPlugin';
import { emptyParagraphPlugin } from './plugins/emptyParagraphPlugin';
import { ToolbarButtons } from './toolbar';
import TextareaField from '../TextareaField';
import { useUploadFile } from '@/api/files/useUploadFile';
import { triggerErrorMessage } from '@/components/ui/toast';

const cx = classNames.bind(styles);

interface MDXEditorFieldProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  autoFocus?: boolean;
  rows?: number;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  readOnlyClassName?: string;
  toolbarPortalTarget?: HTMLElement | null;
  readOnly?: boolean;
}

const MDXEditorField: React.FC<MDXEditorFieldProps> = ({
  value,
  onChange,
  placeholder = '',
  className = '',
  style = {},
  autoFocus = false,
  onBlur,
  onFocus,
  readOnlyClassName = '',
  toolbarPortalTarget,
  readOnly = false,
}) => {
  const [isEditing, setIsEditing] = useState(true); // Start in edit mode by default
  const [localValue, setLocalValue] = useState(value);
  const [isPlainTextMode, setIsPlainTextMode] = useState(false);
  const editorRef = useRef<MDXEditorMethods>(null);
  const displayRef = useRef<HTMLDivElement>(null);
  const prevReadOnlyRef = useRef(readOnly);

  const { uploadFile } = useUploadFile();

  const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

  const handleTogglePlainText = () => {
    setIsPlainTextMode(!isPlainTextMode);
  };

  // Sync local value with prop value when not editing, or when in read-only (version preview) mode,
  // or when exiting read-only mode (back to latest).
  // MDXEditor's `markdown` prop is init-only, so we must call setMarkdown() imperatively.
  useEffect(() => {
    if (value === null) return;
    const wasReadOnly = prevReadOnlyRef.current;
    prevReadOnlyRef.current = readOnly;
    if (readOnly || !isEditing || localValue === null || (wasReadOnly && !readOnly)) {
      setLocalValue(value);
      editorRef.current?.setMarkdown(value);
    }
  }, [value, isEditing, readOnly]);

  // Auto focus on mount if specified
  useEffect(() => {
    if (autoFocus && !readOnly) {
      setIsEditing(true);
    }
  }, [autoFocus, readOnly]);

  const handleClick = (e: React.MouseEvent) => {
    if (readOnly) return;
    const contentEditable = displayRef.current?.querySelector('[contenteditable="true"]') as HTMLElement;

    // Check if click target is inside contentEditable
    const isClickInsideEditor = contentEditable && contentEditable.contains(e.target as Node);

    // Only focus if click was outside contentEditable
    if (!isClickInsideEditor) {
      if (!isEditing) {
        setIsEditing(true);
        // Focus the contentEditable after a short delay to ensure it's rendered
        setTimeout(() => {
          if (contentEditable) {
            contentEditable.focus();
          }
        }, 0);
      } else {
        // If already editing, just focus the contentEditable
        if (contentEditable) {
          contentEditable.focus();
        }
      }
    }
  };

  const handleChange = (newValue: string) => {
    setLocalValue(newValue);
    onChange(newValue);
  };

  const handleFocus = () => {
    if (!readOnly) {
      setIsEditing(true);
    }
    if (onFocus) {
      onFocus();
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isEditing) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEscapeKey(event)) {
        event.preventDefault();
        setLocalValue(value);
        setIsEditing(false);
        if (onBlur) {
          onBlur();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditing, value, onBlur]);

  const isEmpty = !localValue || localValue.trim() === '';
  const showPlaceholder = isEmpty && !isEditing;

  if (localValue === null) {
    return null;
  }

  return (
    <div
      ref={displayRef}
      onClick={handleClick}
      onFocus={handleFocus}
      tabIndex={0}
      onDragOver={(e) => {
        if (!readOnly) e.preventDefault();
      }}
      onDrop={async (e) => {
        if (readOnly) return;

        // If the drop landed inside the contenteditable, the imagePlugin's own
        // DROP_COMMAND handler (registered at COMMAND_PRIORITY_HIGH) takes over.
        // We only handle drops on the outer wrapper here.
        const contentEditable = displayRef.current?.querySelector('[contenteditable="true"]');
        if (contentEditable && contentEditable.contains(e.target as Node)) return;

        e.preventDefault();
        const files = Array.from(e.dataTransfer.files).filter((f) =>
          SUPPORTED_IMAGE_TYPES.includes(f.type),
        );
        for (const file of files) {
          try {
            const url = await uploadFile(file);
            editorRef.current?.insertMarkdown(`![](${url})`);
          } catch {
            // useUploadFile logs the error and notifies the user.
          }
        }
      }}
      className={cx(
        'mdx-editor-note-content',
        { 'cursor-text': !readOnly },
        'break-words',
        'transition-colors',
        'p-0',
        'm-0',
        'flex-1',
        'flex',
        'flex-col',
        'min-h-0',
        {
          'hover:border-gray-400': !isEditing && !readOnly,
          'text-gray-400': showPlaceholder,
        },
        readOnlyClassName,
        className
      )}
      style={style}
    >
      {showPlaceholder ? (
        <p className="text-gray-400 p-3 block">{placeholder}</p>
      ) : isPlainTextMode ? (
        <>
          {toolbarPortalTarget && createPortal(
            <ToolbarButtons
              isPlainTextMode={isPlainTextMode}
              onTogglePlainText={handleTogglePlainText}
            />,
            toolbarPortalTarget
          )}
          <TextareaField
            value={localValue}
            onChange={handleChange}
            placeholder={placeholder}
            className="flex-1 p-3 font-mono text-sm"
            autoFocus={autoFocus}
            onBlur={onBlur}
            onFocus={onFocus}
            readOnly={readOnly}
          />
        </>
      ) : (
        <div className={cx('markdown-body', 'prose', 'prose-sm', 'max-w-none', 'flex-1', 'flex', 'flex-col', 'min-h-0')}>
          <MDXEditor
            ref={editorRef}
            markdown={localValue}
            onChange={handleChange}
            placeholder={placeholder}
            readOnly={readOnly || !isEditing}
            contentEditableClassName="mdx-editor--editable p-0 outline-none"
            plugins={[
              emptyParagraphPlugin(),
              toolbarPlugin({
                toolbarContents: () => {
                  // Render toolbar in portal if target is provided
                  if (toolbarPortalTarget) {
                    return createPortal(
                      <ToolbarButtons
                        isPlainTextMode={isPlainTextMode}
                        onTogglePlainText={handleTogglePlainText}
                      />,
                      toolbarPortalTarget
                    );
                  }
                  return (
                    <ToolbarButtons
                      isPlainTextMode={isPlainTextMode}
                      onTogglePlainText={handleTogglePlainText}
                    />
                  );
                }
              }),
              headingsPlugin(),
              listsPlugin(),
              quotePlugin(),
              thematicBreakPlugin(),
              linkPlugin(),
              linkDialogPlugin(),
              imagePlugin({
                imageUploadHandler: uploadFile,
                disableImageResize: false,
              }),
              tablePlugin(),
              directivesPlugin(),
              secretFieldPlugin(),
              codeBlockPlugin({ defaultCodeBlockLanguage: 'js' }),
              codeMirrorPlugin({ codeBlockLanguages }),
              markdownShortcutPlugin(),
              diffSourcePlugin({ viewMode: 'rich-text', diffMarkdown: '' }),
            ]}
          />
        </div>
      )}
    </div>
  );
};

export default MDXEditorField;
